import { useState, useEffect } from "react";
import React from "react";
import { getFirestore, getDocs, getDoc, setDoc, doc, updateDoc, collection } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import app from "./firebase";
import { useCookies } from "react-cookie";
import { FlutterWaveButton, closePaymentModal } from "flutterwave-react-v3";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import {
  LogOut, Menu, X, ChevronRight, ArrowDownCircle, ArrowUpCircle, CheckCircle2, AlertCircle, Sprout, ShoppingCart, Clock, BookOpen,
  LayoutDashboard, History, Wallet as WalletIcon, Settings, PhoneCall, Leaf, Search
} from "lucide-react";

const db = getFirestore(app);

const NIGERIAN_BANKS = [
  { name: "Access Bank", code: "044" },
  { name: "Access Bank (Diamond)", code: "063" },
  { name: "ALAT by WEMA", code: "035A" },
  { name: "ASO Savings and Loans", code: "401" },
  { name: "Bowen Microfinance Bank", code: "50931" },
  { name: "Carbon", code: "565" },
  { name: "Citibank Nigeria", code: "023" },
  { name: "Ecobank Nigeria", code: "050" },
  { name: "Ekondo Microfinance Bank", code: "562" },
  { name: "Eyowo", code: "50126" },
  { name: "Fidelity Bank", code: "070" },
  { name: "First Bank of Nigeria", code: "011" },
  { name: "First City Monument Bank", code: "214" },
  { name: "Globus Bank", code: "00103" },
  { name: "Guaranty Trust Bank", code: "058" },
  { name: "Hasal Microfinance Bank", code: "50383" },
  { name: "Heritage Bank", code: "030" },
  { name: "Jaiz Bank", code: "301" },
  { name: "Keystone Bank", code: "082" },
  { name: "Kuda Bank", code: "50211" },
  { name: "Moniepoint MFB", code: "50515" },
  { name: "OPay Digital Services Limited (OPay)", code: "999992" },
  { name: "PalmPay", code: "999991" },
  { name: "Parallex Bank", code: "526" },
  { name: "Parkway - ReadyCash", code: "311" },
  { name: "Paycom (OPay)", code: "999992" },
  { name: "Polaris Bank", code: "076" },
  { name: "Providus Bank", code: "101" },
  { name: "Rubies MFB", code: "125" },
  { name: "Sparkle Microfinance Bank", code: "51310" },
  { name: "Stanbic IBTC Bank", code: "039" },
  { name: "Standard Chartered Bank", code: "068" },
  { name: "Sterling Bank", code: "232" },
  { name: "Suntrust Bank", code: "100" },
  { name: "TAJ Bank", code: "302" },
  { name: "TCF MFB", code: "51211" },
  { name: "Titan Bank", code: "102" },
  { name: "Union Bank of Nigeria", code: "032" },
  { name: "United Bank for Africa", code: "033" },
  { name: "Unity Bank", code: "215" },
  { name: "VFD Microfinance Bank Limited", code: "566" },
  { name: "Wema Bank", code: "035" },
  { name: "Zenith Bank", code: "057" }
];

const Wallet = () => {
  const [name, setName] = useState("Investor");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [bank, setBank] = useState("");
  const [acct_number, setAccountnumber] = useState("");
  const [credit_amount, setCreditAmount] = useState("");
  const [credit_account_name, setCreditAccountName] = useState("");
  const [price, setPrice] = useState(0);
  const [toggleDeposit, setToggleDeposit] = useState(false);
  const [toggleWithdraw, setToggleWithdraw] = useState(false);
  const [cookies, , removeCookie] = useCookies(["name"]);
  const [wallet, setWallet] = useState("0.00");
  const [investment, setInvestment] = useState("0.00");
  const [escrow_total, setEscrowTotal] = useState("0.00");
  const [locked_wallet, setLockedAmount] = useState(0);
  const [show_mobile_menu, setMobileMenu] = useState(false);
  const [toast, setToast] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [bankCode, setBankCode] = useState("");
  const [isFrozen, setIsFrozen] = useState(false);

  const userId = cookies.username;
  if (!userId) window.location.href = "/login";

  const showToast = (type, title, msg) => {
    setToast({ type, title, msg });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (show_mobile_menu) document.body.classList.add("lock-scroll");
    else document.body.classList.remove("lock-scroll");
    return () => document.body.classList.remove("lock-scroll");
  }, [show_mobile_menu]);

  useEffect(() => {
    const initiateData = async () => {
      try {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const ud = { id: docSnap.id, ...docSnap.data() };
          setName(ud.name);
          setEmail(ud.email);
          setPhone(ud.phone);
          setIsFrozen(ud.frozen || false);
          setWallet((ud.wallet).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
          setInvestment((ud.investment).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));

          // Fetch Escrow Total
          const marketSnapshot = await getDocs(collection(db, "buyers_request"));
          const userRequests = marketSnapshot.docs
            .map(d => ({ ...d.data(), id: d.id }))
            .filter(item => item.id && item.id.startsWith(userId));

          const total_escrow = userRequests.reduce((sum, item) => sum + (parseFloat(item.escrowAmount) || 0), 0);
          setEscrowTotal(total_escrow.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));

          // Fetch Locked Funds (Seller Sales completed < 12hrs ago)
          const txSnapshot = await getDocs(collection(db, "market_transactions"));
          const holdPeriodLine = new Date(Date.now() - 12 * 60 * 60 * 1000); // 12 hours ago

          let lockedAmt = 0;
          txSnapshot.docs.forEach(d => {
            const tx = d.data();
            if (tx.sellerId === userId && tx.status === "Completed" && tx.completedAt) {
              const compDate = new Date(tx.completedAt);
              if (compDate > holdPeriodLine) {
                lockedAmt += (parseFloat(tx.amount) || 0);
              }
            }
          });
          setLockedAmount(lockedAmt);
        }
      } catch (error) { console.error(error); }
    };
    if (userId) initiateData();
  }, [userId]);

  const config = {
    public_key: "FLWPUBK-1cdf2a43553c803153a0da1b2a09a90c-X",
    tx_ref: Date.now().toString(), amount: price, currency: "NGN",
    payment_options: "card, banktransfer, ussd",
    customer: { email, phonenumber: phone, name },
    customizations: { title: "AGRIVESTIA Deposit", description: "Wallet top-up", logo: "https://yourlogo.com/logo.png" },
  };

  const sendEmail = (message) => {
    emailjs.send("service_pehhhv8", "template_rql77hn", { name, email, message }, "9o1vflADP-wePeEFd")
      .catch(err => console.error("Email failed", err));
  };

  const addFundsToWallet = async () => {
    const amount = parseInt(price);
    const wallet_1 = parseInt(wallet.replaceAll(",", ""));
    const new_val = wallet_1 + amount;
    await updateDoc(doc(db, "users", userId), { wallet: new_val });
    setWallet((new_val).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
    showToast("success", "Deposit Successful", `₦${price} added to your wallet.`);
    sendEmail(`Hi ${name}, a deposit of ₦${price} has been made into your AGRIVESTIA account.`);
    setPrice(0);
  };

  const resolveAccount = async (account_number, bank_code) => {
    if (account_number.length !== 10 || !bank_code) return;
    setIsVerifying(true);
    try {
      const secret_key = process.env.REACT_APP_FLW_SECRET_KEY;
      if (!secret_key) {
        console.warn("Flutterwave secret key not found in environment variables.");
        setIsVerifying(false);
        return;
      }
      const response = await fetch("https://api.flutterwave.com/v3/accounts/resolve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${secret_key}`
        },
        body: JSON.stringify({ account_number, account_bank: bank_code })
      });
      const data = await response.json();
      if (data.status === "success") {
        setCreditAccountName(data.data.account_name);
        showToast("success", "Account Verified", `Account belongs to ${data.data.account_name}`);
      } else {
        showToast("error", "Verification Failed", data.message || "Could not verify account name.");
      }
    } catch (error) {
      console.error("Error resolving account:", error);
      showToast("error", "Verification Error", "An error occurred while verifying the account.");
    } finally {
      setIsVerifying(false);
    }
  };

  const withdraw = async () => {
    if (!acct_number || !credit_account_name || !credit_amount || !bank || !bankCode) {
      showToast("error", "Incomplete form", "Please fill in all withdrawal fields. Select your bank again if missing."); return;
    }
    if (isFrozen) {
      showToast("error", "Account Frozen", "Withdrawals are currently disabled for this account by an admin."); return;
    }

    const amount = parseInt(credit_amount);
    const wallet_raw = parseFloat(wallet.replaceAll(",", ""));
    const available_funds = wallet_raw - locked_wallet;

    if (amount <= available_funds) {
      showToast("success", "Processing", "Initiating secure bank transfer...");
      try {
        const functions = getFunctions(app);
        const processWithdrawal = httpsCallable(functions, "processWithdrawal");

        const result = await processWithdrawal({
          bankCode: bankCode,
          accountNumber: acct_number,
          amount: amount,
          name: name,
          secretKey: process.env.REACT_APP_FLW_SECRET_KEY
        });

        const data = result.data;

        if (data.status === "success") {
          const new_val = wallet_raw - amount;
          await setDoc(doc(doc(db, "withdraw", userId), "requests", Date.now().toString()), { bank, bankCode, name: credit_account_name, amount: credit_amount, acct_number, status: "processing", flw_ref: data.flw_ref || "secure-func-ref", date: new Date(), userId });
          await updateDoc(doc(db, "users", userId), { wallet: new_val });
          setWallet((new_val).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,"));
          showToast("success", "Withdrawal Dispatched", "Your funds are actively routing to your bank account.");
          sendEmail(`Hi ${name}, a withdrawal of ₦${credit_amount} has been securely processed from your AGRIVESTIA wallet into your bank account.`);
          setToggleWithdraw(false);
          setBank("");
          setBankCode("");
          setAccountnumber("");
          setCreditAccountName("");
          setCreditAmount("");
        }
      } catch (err) {
        console.error("Cloud Function Error:", err);
        showToast("error", "Transfer Blocked", err.message || "A secure connection error occurred while requesting withdrawal.");
      }
    } else {
      if (amount <= wallet_raw) {
        showToast("error", "Funds Held", `₦${locked_wallet.toLocaleString()} is currently locked safely for 12 hours. Available withdrawable balance: ₦${available_funds.toLocaleString()}`);
      } else {
        showToast("error", "Insufficient Funds", "You do not have this amount available in your wallet.");
      }
    }
  };

  const navItems = [
    { icon: <LayoutDashboard size={18} />, label: "Dashboard", action: () => { window.location.href = "/dashboard"; } },
    { icon: <History size={18} />, label: "Investment History", action: () => { window.location.href = "/history"; } },
    { icon: <WalletIcon size={18} />, label: "Wallet", action: null, active: true },
    { icon: <Settings size={18} />, label: "Settings", action: () => { window.location.href = "/settings"; } },
    { icon: <ShoppingCart size={18} />, label: "Marketplace", action: () => { window.location.href = "/market_dashboard"; } },
    { icon: <BookOpen size={18} />, label: "Health & News (Blog)", action: () => { window.location.href = "/blog"; } },
    { icon: <PhoneCall size={18} />, label: "Contact Support", action: () => { window.location.href = "/contact"; } },
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", display: "flex", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet><title>Wallet — Agrivestia</title></Helmet>

      {/* Toast */}
      {toast && (
        <div className="toast" style={{ borderLeft: `4px solid ${toast.type === "success" ? "var(--color-success)" : "var(--color-danger)"}` }}>
          <div className={`toast-icon ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
            {toast.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          </div>
          <div className="toast-body"><div className="toast-title">{toast.title}</div><div className="toast-msg">{toast.msg}</div></div>
          <button className="toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      {show_mobile_menu && <div className="overlay" onClick={() => setMobileMenu(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${show_mobile_menu ? "open" : ""}`} style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid var(--color-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Leaf size={17} color="white" /></div>
            <span style={{ fontWeight: 800, fontSize: "17px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "15px", color: "white" }}>{name ? name[0].toUpperCase() : "U"}</div>
            <div><div style={{ fontWeight: 600, fontSize: "13px", color: "var(--color-gray-800)" }}>{name}</div><div style={{ fontSize: "11px", color: "var(--color-gray-400)" }}>Agrivestia Farmer</div></div>
          </div>
        </div>
        <div style={{ margin: "16px", padding: "14px 16px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-accent-light)" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-primary)", letterSpacing: "0.05em", marginBottom: "4px" }}>WALLET BALANCE</div>
          <div style={{ fontSize: "18px", fontWeight: 800, color: "var(--color-primary-dark)" }}>₦{wallet}</div>
          {locked_wallet > 0 && (
            <div style={{ fontSize: "10px", fontWeight: 600, color: "var(--color-primary)", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
              <Clock size={10} /> ₦{locked_wallet.toLocaleString()} Holding
            </div>
          )}
          <div style={{ fontSize: "10px", fontWeight: 500, color: "var(--color-gray-500)", marginTop: "8px", textTransform: "uppercase" }}>Escrowed: ₦{escrow_total}</div>
        </div>
        <nav style={{ padding: "0 12px", flex: 1, overflowY: "auto" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", letterSpacing: "0.07em", textTransform: "uppercase", padding: "4px 8px 10px" }}>Menu</div>
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action ? () => { setMobileMenu(false); item.action(); } : undefined}
              className={`sidebar-link ${item.active ? "active" : ""}`} style={{ width: "100%", border: "none", cursor: "pointer", marginBottom: "2px" }}>
              <span>{item.icon}</span><span style={{ fontSize: "14px" }}>{item.label}</span>
              {item.active && <ChevronRight size={14} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 12px 20px", borderTop: "1px solid var(--color-gray-100)" }}>
          <button className="sidebar-link danger" onClick={() => { removeCookie("username"); window.location.href = "/login"; }} style={{ width: "100%", border: "none", cursor: "pointer" }}>
            <LogOut size={17} /><span style={{ fontSize: "14px" }}>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Topbar */}
        <div style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", padding: "0 16px", minHeight: "64px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          {/* Mobile Menu & Logo (Left) */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Mobile hamburger */}
            <button onClick={() => setMobileMenu(!show_mobile_menu)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-600)", display: "none", padding: "4px" }}
              className="show-mobile">
              {show_mobile_menu ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="show-mobile" style={{ display: "none", alignItems: "center", gap: "8px" }}
              onClick={() => window.location.href = "/"}>
              <div style={{ width: "30px", height: "30px", borderRadius: "8px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Leaf size={15} color="white" />
              </div>
              <span style={{ fontWeight: 800, fontSize: "14px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="hide-mobile">
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)" }}>My Wallet</div>
              <div style={{ fontSize: "12px", color: "var(--color-gray-400)" }}>Manage deposits and withdrawals</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", gap: "10px" }} className="hide-mobile">
              <button className="btn-primary" onClick={() => { setToggleDeposit(!toggleDeposit); setToggleWithdraw(false); }} style={{ gap: "6px", padding: "9px 18px", fontSize: "13px" }}>
                <ArrowDownCircle size={15} /> Deposit
              </button>
              <button className="btn-ghost" onClick={() => { setToggleWithdraw(!toggleWithdraw); setToggleDeposit(false); }} style={{ gap: "6px", padding: "9px 18px", fontSize: "13px" }}>
                <ArrowUpCircle size={15} /> Withdraw
              </button>
            </div>

            <div className="show-mobile" style={{ gap: "8px" }}>
              <button className="btn-primary" onClick={() => { setToggleDeposit(!toggleDeposit); setToggleWithdraw(false); }} style={{ padding: "8px", minWidth: "36px", height: "36px", display: "flex", justifyContent: "center" }}>
                <ArrowDownCircle size={18} />
              </button>
              <button className="btn-ghost" onClick={() => { setToggleWithdraw(!toggleWithdraw); setToggleDeposit(false); }} style={{ padding: "8px", minWidth: "36px", height: "36px", display: "flex", justifyContent: "center" }}>
                <ArrowUpCircle size={18} />
              </button>
            </div>


          </div>
        </div>

        <div style={{ padding: "28px" }}>
          {/* Stat Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "28px" }}>
            <div className="card-stat" style={{ background: "var(--gradient-primary)", color: "white" }}>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Wallet Balance</div>
                <div style={{ fontSize: "28px", fontWeight: 800 }}>₦{wallet}</div>
                {locked_wallet > 0 && (
                  <div style={{ fontSize: "10px", marginTop: "4px", color: "rgba(255,255,255,0.8)", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                    <Clock size={10} /> ₦{locked_wallet.toLocaleString()} on 12hr hold
                  </div>
                )}
              </div>
              <WalletIcon size={36} color="rgba(255,255,255,0.12)" style={{ position: "absolute", bottom: "16px", right: "16px" }} />
            </div>
            <div className="card-stat">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "var(--gradient-accent)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-500)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Farm Balance</div>
              <div style={{ fontSize: "26px", fontWeight: 800, color: "var(--color-gray-900)" }}>₦{investment}</div>
              <Sprout size={32} color="rgba(26,143,70,0.08)" style={{ position: "absolute", bottom: "16px", right: "16px" }} />
            </div>
            <div className="card-stat">
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "linear-gradient(135deg, #f59e0b, #fbbf24)", borderRadius: "var(--radius-lg) var(--radius-lg) 0 0" }} />
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-gray-500)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Escrowed Balance</div>
              <div style={{ fontSize: "26px", fontWeight: 800, color: "var(--color-gray-900)" }}>₦{escrow_total}</div>
              <ShoppingCart size={32} color="rgba(245,158,11,0.08)" style={{ position: "absolute", bottom: "16px", right: "16px" }} />
            </div>
          </div>

          {/* Deposit Panel */}
          {toggleDeposit && (
            <div className="card" style={{ padding: "24px", marginBottom: "24px", animation: "fadeInUp 0.3s ease", maxWidth: "500px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Deposit Funds</div>
                  <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "2px" }}>Securely add funds via Flutterwave</div>
                </div>
                <button onClick={() => setToggleDeposit(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}><X size={18} /></button>
              </div>
              <div className="input-group">
                <label className="input-label">Amount (₦)</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", fontWeight: "600", color: "var(--color-gray-400)" }}>₦</span>
                  <input className="input-field" type="number" value={price === 0 ? "" : price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 5000" style={{ paddingLeft: "40px" }} />
                </div>
              </div>
              <FlutterWaveButton {...config} text="Pay with Flutterwave"
                callback={(response) => {
                  const isSuccess = response.status === "completed" && (response.charge_response_code === "00" || response.charge_response_code === "0");
                  if (isSuccess) addFundsToWallet();
                  closePaymentModal();
                }}
                onClose={() => { }}
                style={{ marginTop: "8px", width: "100%", background: "var(--gradient-accent)", color: "white", border: "none", borderRadius: "var(--radius-md)", padding: "13px", fontWeight: 700, fontSize: "15px", cursor: "pointer", fontFamily: "var(--font-sans)" }}
              />
            </div>
          )}

          {/* Withdraw Panel */}
          {toggleWithdraw && (
            <div className="card" style={{ padding: "24px", marginBottom: "24px", animation: "fadeInUp 0.3s ease", maxWidth: "500px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--color-gray-800)" }}>Bank Withdrawal</div>
                  <div style={{ fontSize: "12px", color: "var(--color-gray-400)", marginTop: "2px" }}>Ensure account carries your registered name</div>
                </div>
                <button onClick={() => setToggleWithdraw(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}><X size={18} /></button>
              </div>
              <div className="input-group">
                <label className="input-label">Bank Name</label>
                <select className="input-field" value={bank} onChange={e => {
                  const selectedBankName = e.target.value;
                  setBank(selectedBankName);
                  const selectedBankCode = NIGERIAN_BANKS.find(b => b.name === selectedBankName)?.code;
                  setBankCode(selectedBankCode);
                  if (acct_number.length === 10) resolveAccount(acct_number, selectedBankCode);
                }}>
                  <option value="">Select Bank</option>
                  {NIGERIAN_BANKS.map(b => (
                    <option key={b.code} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">Account Number</label>
                <div style={{ position: "relative" }}>
                  <input className="input-field" type="text" maxLength={10} value={acct_number} onChange={e => {
                    const val = e.target.value.replace(/\D/g, "");
                    setAccountnumber(val);
                    if (val.length === 10 && bankCode) resolveAccount(val, bankCode);
                  }} placeholder="10-digit number" />
                  {isVerifying && <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" }} className="spin"><Search size={14} color="var(--color-gray-400)" /></div>}
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Account Name</label>
                <input className="input-field" type="text" value={credit_account_name} onChange={e => setCreditAccountName(e.target.value)} placeholder="Full name on account" />
              </div>

              <div className="input-group">
                <label className="input-label">Amount (₦)</label>
                <input className="input-field" type="number" value={credit_amount} onChange={e => setCreditAmount(e.target.value)} placeholder="Amount to withdraw" />
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                <button className="btn-danger" onClick={withdraw} style={{ flex: 1, justifyContent: "center", padding: "12px" }}>Request Withdrawal</button>
                <button className="btn-ghost" onClick={() => setToggleWithdraw(false)} style={{ flex: 1, justifyContent: "center", padding: "12px" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Info panel */}
          {!toggleDeposit && !toggleWithdraw && (
            <div className="card" style={{ padding: "28px", maxWidth: "500px", textAlign: "center" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "var(--color-primary-light)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <WalletIcon size={24} color="var(--color-primary)" />
              </div>
              <div style={{ fontWeight: 700, fontSize: "16px", color: "var(--color-gray-800)", marginBottom: "8px" }}>Manage Your Wallet</div>
              <div style={{ fontSize: "13px", color: "var(--color-gray-400)", lineHeight: 1.7, marginBottom: "20px" }}>
                Use the Deposit button to add funds via Flutterwave (cards, bank transfer, USSD), or Withdraw to initiate a bank transfer.
              </div>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <button className="btn-primary" onClick={() => setToggleDeposit(true)} style={{ padding: "10px 20px", fontSize: "13px", gap: "6px" }}>
                  <ArrowDownCircle size={15} /> Deposit
                </button>
                <button className="btn-ghost" onClick={() => setToggleWithdraw(true)} style={{ padding: "10px 20px", fontSize: "13px", gap: "6px" }}>
                  <ArrowUpCircle size={15} /> Withdraw
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
      `}</style>
    </div>
  );
};

export default Wallet;
